import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Params, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Observable } from 'rxjs';

import type { IOnboardingStatusResponse } from '../../../auth/interfaces';
import { AuthService } from '../../../auth/services/auth/auth.service';
import type { IOnboardingResponse } from '../../shared/models/onboarding-common.interface';
import type { IErrorResponse } from '../../shared/models/onboarding-common.interface';
import {
  SPONSOR_ONBOARDING_AUDIENCE_STEP_NUMBER,
  SPONSOR_ONBOARDING_BUDGET_DURATION_STEP_NUMBER,
  SPONSOR_ONBOARDING_COMPANY_ACTIONS_STEP_NUMBER,
  SPONSOR_ONBOARDING_GEOGRAPHY_STEP_NUMBER,
  SPONSOR_ONBOARDING_GOALS_STEP_NUMBER,
  SPONSOR_ONBOARDING_PLANNED_TOTAL_STEPS,
  SPONSOR_ONBOARDING_PREFERENCES_STEP_NUMBER,
  SPONSOR_ONBOARDING_SECTOR_STEP_NUMBER,
  SPONSOR_ONBOARDING_SUMMARY_STEP_NUMBER,
} from '../constants/sponsor-onboarding.constants';
import { SponsorAvoidPreference } from '../enums/sponsor-avoid-preference.enum';
import { SponsorCompanyAction } from '../enums/sponsor-company-action.enum';
import { SponsorSportInterest } from '../enums/sponsor-sport-interest.enum';
import type { ISponsorAudienceStepData } from '../models/sponsor-audience-step-data.interface';
import type { ISponsorCampaignBudgetStepData } from '../models/sponsor-campaign-budget-step-data.interface';
import type { ISponsorCompanyActionsStepData } from '../models/sponsor-company-actions-step-data.interface';
import type { ISponsorExplorerDefaults } from '../models/sponsor-explorer-defaults.interface';
import type { ISponsorGeographyStepData } from '../models/sponsor-geography-step-data.interface';
import type { ISponsorGoalsStepData } from '../models/sponsor-goals-step-data.interface';
import type { ISponsorOnboardingDraft } from '../models/sponsor-onboarding-draft.interface';
import type { ISponsorOnboardingSaveResult } from '../models/sponsor-onboarding-save-result.interface';
import type { ISponsorPreferencesStepData } from '../models/sponsor-preferences-step-data.interface';
import type { ISponsorSectorStepData } from '../models/sponsor-sector-step-data.interface';
import { SponsorOnboardingDraftService } from './sponsor-onboarding-draft.service';
import { SponsorOnboardingHydrateService } from './sponsor-onboarding-hydrate.service';
import { SponsorOnboardingSaveService } from './sponsor-onboarding-save.service';
import { SponsorOnboardingSelectionService } from './sponsor-onboarding-selection.service';
import { SponsorOnboardingValidationService } from './sponsor-onboarding-validation.service';

@Injectable({
  providedIn: 'root',
})
export class SponsorOnboardingService {
  private readonly authService = inject(AuthService);
  private readonly draftService = inject(SponsorOnboardingDraftService);
  private readonly hydrateService = inject(SponsorOnboardingHydrateService);
  private readonly saveService = inject(SponsorOnboardingSaveService);
  private readonly selectionService = inject(SponsorOnboardingSelectionService);
  private readonly validationService = inject(SponsorOnboardingValidationService);

  public readonly draft = this.draftService.draft;
  public readonly saving = this.saveService.saving;
  public readonly statusLoaded = this.hydrateService.statusLoaded;
  public readonly statusHydrationFailed = this.hydrateService.statusHydrationFailed;
  public readonly statusCompleted = this.hydrateService.statusCompleted;
  public readonly statusCurrentStep = this.hydrateService.statusCurrentStep;
  public readonly redirectingToDashboard = this.saveService.redirectingToDashboard;

  public readonly goalsStepNumber = SPONSOR_ONBOARDING_GOALS_STEP_NUMBER;
  public readonly audienceStepNumber = SPONSOR_ONBOARDING_AUDIENCE_STEP_NUMBER;
  public readonly geographyStepNumber = SPONSOR_ONBOARDING_GEOGRAPHY_STEP_NUMBER;
  public readonly sectorStepNumber = SPONSOR_ONBOARDING_SECTOR_STEP_NUMBER;
  public readonly budgetDurationStepNumber = SPONSOR_ONBOARDING_BUDGET_DURATION_STEP_NUMBER;
  public readonly companyActionsStepNumber = SPONSOR_ONBOARDING_COMPANY_ACTIONS_STEP_NUMBER;
  public readonly preferencesStepNumber = SPONSOR_ONBOARDING_PREFERENCES_STEP_NUMBER;
  public readonly summaryStepNumber = SPONSOR_ONBOARDING_SUMMARY_STEP_NUMBER;
  public readonly plannedTotalSteps = SPONSOR_ONBOARDING_PLANNED_TOTAL_STEPS;

  public constructor() {
    const destroyRef = inject(DestroyRef);
    this.authService.sessionInvalidated$.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => {
      this.resetStatusSession();
    });
  }

  public patchGoals(partial: Partial<ISponsorGoalsStepData>): void {
    this.draftService.patchGoals(partial);
  }

  public patchAudience(partial: Partial<ISponsorAudienceStepData>): void {
    this.draftService.patchAudience(partial);
  }

  public patchGeography(partial: Partial<ISponsorGeographyStepData>): void {
    this.draftService.patchGeography(partial);
  }

  public patchSector(partial: Partial<ISponsorSectorStepData>, options?: { replace?: boolean }): void {
    this.draftService.patchSector(partial, options);
  }

  public patchCampaignBudget(partial: Partial<ISponsorCampaignBudgetStepData>): void {
    this.draftService.patchCampaignBudget(partial);
  }

  public patchCompanyActions(partial: Partial<ISponsorCompanyActionsStepData>): void {
    this.draftService.patchCompanyActions(partial);
  }

  public patchPreferences(partial: Partial<ISponsorPreferencesStepData>): void {
    this.draftService.patchPreferences(partial);
  }

  public restoreDraft(): void {
    this.draftService.restoreDraft();
  }

  public retryStatusHydration(): Observable<boolean> {
    return this.hydrateService.retryStatusHydration();
  }

  public markStatusHydrationFailed(): void {
    this.hydrateService.markStatusHydrationFailed();
  }

  public refreshStatus(): Observable<boolean> {
    return this.hydrateService.refreshStatus();
  }

  public hydrateFromStatus(options?: { forceRefresh?: boolean }): Observable<ISponsorOnboardingDraft> {
    return this.hydrateService.hydrateFromStatus(options);
  }

  public prepareStepFromStatus(options?: { forceRefresh?: boolean }): Observable<ISponsorOnboardingDraft> {
    return this.hydrateService.prepareStepFromStatus(options);
  }

  public ensureStatusLoaded(options?: { forceRefresh?: boolean }): Observable<boolean> {
    return this.hydrateService.ensureStatusLoaded(options);
  }

  public getOnboardingState(): Observable<IOnboardingStatusResponse> {
    return this.hydrateService.getOnboardingState();
  }

  public getResumeStepPath(): string {
    return this.validationService.getResumeStepPath();
  }

  public navigateToStep(router: Router, path: string, queryParams: Params): void {
    void router.navigate([path], { queryParams });
  }

  public displaySaveError(
    messageService: MessageService,
    translate: TranslateService,
    errorTranslationKey?: string,
  ): void {
    const key = errorTranslationKey ?? 'onboarding.sponsorOnboarding.errors.saveFailed';
    const translated = translate.instant(key);
    const detail =
      translated !== key
        ? translated
        : translate.instant('onboarding.sponsorOnboarding.errors.saveFailed');
    messageService.add({
      severity: 'error',
      summary: translate.instant('onboarding.sponsorOnboarding.errors.saveFailedSummary'),
      detail,
      life: 5000,
    });
  }

  public saveGoalsStep(): Observable<ISponsorOnboardingSaveResult> {
    return this.saveService.saveGoalsStep();
  }

  public saveAudienceStep(): Observable<ISponsorOnboardingSaveResult> {
    return this.saveService.saveAudienceStep();
  }

  public saveGeographyStep(): Observable<ISponsorOnboardingSaveResult> {
    return this.saveService.saveGeographyStep();
  }

  public saveSectorStep(): Observable<ISponsorOnboardingSaveResult> {
    return this.saveService.saveSectorStep();
  }

  public saveCampaignBudgetStep(): Observable<ISponsorOnboardingSaveResult> {
    return this.saveService.saveCampaignBudgetStep();
  }

  public saveCompanyActionsStep(): Observable<ISponsorOnboardingSaveResult> {
    return this.saveService.saveCompanyActionsStep();
  }

  public savePreferencesStep(): Observable<ISponsorOnboardingSaveResult> {
    return this.saveService.savePreferencesStep();
  }

  public finalizeOnboarding(): Observable<IOnboardingResponse> {
    return this.saveService.finalizeOnboarding();
  }

  public clearRedirectingToDashboard(): void {
    this.saveService.clearRedirectingToDashboard();
  }

  public resolveErrorTranslationKey(err: IErrorResponse, fallbackKey: string): string {
    return this.saveService.resolveErrorTranslationKey(err, fallbackKey);
  }

  public extractBackendErrorMessage(err: IErrorResponse): string | null {
    return this.saveService.extractBackendErrorMessage(err);
  }

  public buildGoalsStepData(draft?: ISponsorOnboardingDraft): Record<string, unknown> {
    return this.saveService.buildGoalsStepData(draft);
  }

  public buildAudienceStepData(draft?: ISponsorOnboardingDraft): Record<string, unknown> {
    return this.saveService.buildAudienceStepData(draft);
  }

  public buildGeographyStepData(draft?: ISponsorOnboardingDraft): Record<string, unknown> {
    return this.saveService.buildGeographyStepData(draft);
  }

  public buildSectorStepData(draft?: ISponsorOnboardingDraft): Record<string, unknown> {
    return this.saveService.buildSectorStepData(draft);
  }

  public buildCampaignBudgetStepData(draft?: ISponsorOnboardingDraft): Record<string, unknown> {
    return this.saveService.buildCampaignBudgetStepData(draft);
  }

  public buildCompanyActionsStepData(draft?: ISponsorOnboardingDraft): Record<string, unknown> {
    return this.saveService.buildCompanyActionsStepData(draft);
  }

  public buildPreferencesStepData(draft?: ISponsorOnboardingDraft): Record<string, unknown> {
    return this.saveService.buildPreferencesStepData(draft);
  }

  public resolveCompanyActionSelection(
    current: SponsorCompanyAction[],
    clicked: SponsorCompanyAction,
  ): SponsorCompanyAction[] {
    return this.selectionService.resolveCompanyActionSelection(current, clicked);
  }

  public resolveAvoidPreferenceSelection(
    current: SponsorAvoidPreference[],
    clicked: SponsorAvoidPreference,
  ): SponsorAvoidPreference[] {
    return this.selectionService.resolveAvoidPreferenceSelection(current, clicked);
  }

  public resolveSportsInterestSelection(
    current: SponsorSportInterest[],
    clicked: SponsorSportInterest,
  ): SponsorSportInterest[] {
    return this.selectionService.resolveSportsInterestSelection(current, clicked);
  }

  public getDisabledCompanyActionValues(selected: SponsorCompanyAction[]): SponsorCompanyAction[] {
    return this.selectionService.getDisabledCompanyActionValues(selected);
  }

  public getDisabledSportsInterestValues(selected: SponsorSportInterest[]): SponsorSportInterest[] {
    return this.selectionService.getDisabledSportsInterestValues(selected);
  }

  public getDisabledAvoidPreferenceValues(
    selected: SponsorAvoidPreference[],
  ): SponsorAvoidPreference[] {
    return this.selectionService.getDisabledAvoidPreferenceValues(selected);
  }

  public getFirstIncompleteStepPath(draft?: ISponsorOnboardingDraft): string | null {
    return this.validationService.getFirstIncompleteStepPath(draft);
  }

  public isOnboardingReadyForSummary(draft?: ISponsorOnboardingDraft): boolean {
    return this.validationService.isOnboardingReadyForSummary(draft);
  }

  public isGoalsStepValid(draft?: ISponsorOnboardingDraft): boolean {
    return this.validationService.isGoalsStepValid(draft);
  }

  public isAudienceStepValid(draft?: ISponsorOnboardingDraft): boolean {
    return this.validationService.isAudienceStepValid(draft);
  }

  public isGeographyStepValid(draft?: ISponsorOnboardingDraft): boolean {
    return this.validationService.isGeographyStepValid(draft);
  }

  public isSectorStepValid(draft?: ISponsorOnboardingDraft): boolean {
    return this.validationService.isSectorStepValid(draft);
  }

  public isCampaignBudgetStepValid(draft?: ISponsorOnboardingDraft): boolean {
    return this.validationService.isCampaignBudgetStepValid(draft);
  }

  public isCompanyActionsStepValid(draft?: ISponsorOnboardingDraft): boolean {
    return this.validationService.isCompanyActionsStepValid(draft);
  }

  public readExplorerDefaults(): ISponsorExplorerDefaults | null {
    return this.draftService.readExplorerDefaults();
  }

  public clearExplorerDefaults(): void {
    this.draftService.clearExplorerDefaults();
  }

  public resetStatusSession(): void {
    this.saveService.clearRedirectingToDashboard();
    this.hydrateService.resetStatusSession();
  }

  public clearDraft(): void {
    this.draftService.clearDraft();
    this.resetStatusSession();
  }
}
